import { cssRaw, style } from 'typestyle';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import StyleConstants from '../../../core/styles.constants';

/**
 * Styling for the two date pickers in the reservations dialog.
 *
 * The vendor stylesheets are imported here rather than relied on: today they happen to reach
 * the bundle through the statistics page, which is the only other screen using a picker, so
 * this dialog's calendar is styled by a file it has nothing to do with. That page's rules are
 * also global and written for the dashboard theme, which is why an unclassed calendar dropped
 * into this dialog came out as a mix of vendor defaults and somebody else's design.
 *
 * Every rule hangs off [datePicker], the class on the picker root, for two reasons.
 *
 * The popup wrapper (`.react-date-picker__calendar`) cannot be given a class of its own:
 * `calendarClassName` is applied to the calendar *card* inside it (`.react-calendar`) — see
 * `DatePicker.js` — so a rule for the wrapper has to be reached through an ancestor. Styling
 * only the card is what left the wrapper at its vendor width of 350px around a 280px card:
 * ~70px of transparent wrapper down the right, showing the dialog's white paper through.
 *
 * Scoping under the root class also settles specificity: those global dashboard rules are
 * shaped `.react-date-picker__calendar .react-calendar…`, two classes, the same as a naive
 * local rule — so a tie would be broken by injection order, and the statistics page is
 * imported after this file. Three classes wins outright.
 *
 * Nothing here positions the popup: `react-date-picker` places it next to its input through
 * `react-fit`, and the only reason it was ever clipped is that an ancestor was a scroll box.
 * That is fixed on the dialog itself. Passing `portalContainer` instead looks like the obvious
 * fix and is not: it swaps `react-fit` out for a bare `createPortal` with no positioning at
 * all, which drops an unpositioned calendar at the end of `<body>`, behind the backdrop.
 */

/**
 * Above everything else in the dialog.
 *
 * The table's sticky header is the one that matters: MUI gives those cells `z-index: 2` and an
 * opaque background, so at the vendor's `z-index: 1` the header painted a white band straight
 * across the open calendar. Well clear of that, and still local — the popup stays inside
 * `DialogContent`, so it never has to compete with MUI's Modal layer.
 */
const ABOVE_DIALOG_CONTENT = 20;

/** Wide enough for seven day columns at a readable size, narrow enough to sit over the dialog. */
const CALENDAR_WIDTH = 280;

const datePicker = style({
  $nest: {
    '& .react-date-picker__wrapper': {
      border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
      borderRadius: 6,
      padding: '4px 8px',
      backgroundColor: StyleConstants.WHITE_COLOR,
    },
    '& .react-date-picker__inputGroup': {
      fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
      fontSize: 13,
      color: StyleConstants.GREY_COLOR,
      minWidth: 92,
    },
    '& .react-date-picker__calendar-button': {
      padding: '0 0 0 4px',
    },
  },
});

// Raw descendant rules rather than $nest: react-calendar renders its own class names inside a
// wrapper this file cannot put a class on, and these have to out-specify both the vendor
// stylesheet and the statistics page's global rules.
cssRaw(`
  /* The wrapper, which vendor CSS sizes at 350px and gives z-index 1. Both are wrong here: the
     card inside is narrower, so the surplus showed the dialog's white paper through, and the
     table's sticky header (z-index 2) painted straight over a popup left at 1. */
  .${datePicker} .react-date-picker__calendar {
    width: ${CALENDAR_WIDTH}px;
    max-width: 100vw;
    z-index: ${ABOVE_DIALOG_CONTENT};
  }

  .${datePicker} .react-date-picker__calendar .react-calendar {
    width: 100%;
    padding: 12px;
    border: 1px solid ${StyleConstants.LIGHT_GREY_COLOR};
    border-radius: 10px;
    background: ${StyleConstants.WHITE_COLOR};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    font-family: ${StyleConstants.PRIMARY_FONT_FAMILY};
    line-height: 1.4;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__navigation {
    height: 32px;
    margin-bottom: 6px;
    display: flex;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__navigation button {
    min-width: 30px;
    background: none;
    border: none;
    border-radius: 6px;
    color: ${StyleConstants.DARK_BLUE_COLOR};
    font-family: ${StyleConstants.PRIMARY_FONT_FAMILY};
    font-size: 13px;
    font-weight: 600;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__navigation button:enabled:hover,
  .${datePicker} .react-date-picker__calendar .react-calendar__navigation button:enabled:focus {
    background-color: rgba(38, 69, 156, 0.08);
  }

  /* Days before today are disabled by minDate. They stay visible but plainly out of reach —
     removing them would leave a ragged first week nobody can read a month from. */
  .${datePicker} .react-date-picker__calendar .react-calendar__navigation button:disabled,
  .${datePicker} .react-date-picker__calendar .react-calendar__tile:disabled {
    background: transparent;
    color: ${StyleConstants.LIGHT_GREY_COLOR};
    cursor: not-allowed;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__month-view__weekdays {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    color: ${StyleConstants.GREY_COLOR};
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__month-view__weekdays abbr {
    text-decoration: none;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__tile {
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: ${StyleConstants.GREY_COLOR};
    font-family: ${StyleConstants.PRIMARY_FONT_FAMILY};
    font-size: 12.5px;
    font-weight: 500;
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__tile:enabled:hover,
  .${datePicker} .react-date-picker__calendar .react-calendar__tile:enabled:focus {
    background-color: rgba(38, 69, 156, 0.08);
    color: ${StyleConstants.DARK_BLUE_COLOR};
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__tile--now {
    background: rgba(255, 182, 62, 0.25);
    color: ${StyleConstants.GREY_COLOR};
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__tile--active,
  .${datePicker} .react-date-picker__calendar .react-calendar__tile--active:enabled:hover,
  .${datePicker} .react-date-picker__calendar .react-calendar__tile--active:enabled:focus {
    background: ${StyleConstants.DARK_BLUE_COLOR};
    color: ${StyleConstants.WHITE_COLOR};
  }

  .${datePicker} .react-date-picker__calendar .react-calendar__month-view__days__day--neighboringMonth {
    color: ${StyleConstants.LIGHT_GREY_COLOR};
  }
`);

export default datePicker;
